import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useHouseholdOnboarding } from '../hooks/useHouseholdOnboarding';
import { HouseholdOnboardingWizard } from '../components/HouseholdOnboardingWizard';

interface AuthGuardProps {
  children: ReactNode;
  /** Shown while checking authentication (default: loading spinner) */
  fallback?: ReactNode;
  /** Where to redirect if not authenticated (default: '/sign-in') */
  redirectTo?: string;
}

/**
 * Wraps protected content. Redirects to sign-in if user is not authenticated.
 * Shows household onboarding wizard for new users without a household.
 * Uses react-router Navigate for client-side redirect.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <DashboardPage />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({ children, fallback, redirectTo = '/sign-in' }: AuthGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { shouldShow, checking, complete, skip } = useHouseholdOnboarding();

  const loading = authLoading || checking;

  if (loading) {
    return (
      fallback ?? (
        <div data-testid="auth-loading" className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      )
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Show household onboarding for new users
  if (shouldShow) {
    return <HouseholdOnboardingWizard onComplete={complete} onSkip={skip} />;
  }

  return <>{children}</>;
}
